import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  ExpandableSection,
  Header,
} from "@amzn/awsui-components-react";
import {
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { comparisonLabel } from "./omsReportFormatting";

function BoundedList({
  items,
  label,
  ordered = false,
}: {
  items: React.ReactNode[];
  label: string;
  ordered?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, 4);
  const List = ordered ? "ol" : "ul";
  return (
    <>
      <List
        className="oms-settings-summary__list"
        aria-label={label}
        tabIndex={0}
      >
        {visible.map((item, index) => (
          // Repeated conditions are valid and their order is significant to the review.
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>{item}</li>
        ))}
      </List>
      {items.length > 4 && (
        <Button onClick={() => setExpanded(!expanded)}>
          {expanded
            ? `Show fewer ${label}`
            : `Show all ${items.length} ${label}`}
        </Button>
      )}
    </>
  );
}

export default function OMSSettingsSummary({
  report,
  mode,
  filters,
  operation,
  columns,
  allColumns,
  incomplete = 0,
  result = false,
  standalone = false,
  textCaseSensitive = true,
}: {
  report: OmsReport;
  mode: "full" | "filtered";
  filters: OmsRecordFilter[];
  operation: "and" | "or";
  columns: string[];
  allColumns: boolean;
  incomplete?: number;
  result?: boolean;
  standalone?: boolean;
  textCaseSensitive?: boolean;
}) {
  const [resultExpanded, setResultExpanded] = useState(false);
  const matching =
    operation === "or" ? "Any condition (OR)" : "All conditions (AND)";
  const contents = (
    <div
      className={`oms-settings-summary__sections${
        standalone ? " oms-settings-summary__sections--review" : ""
      }`}
    >
      <section aria-label="Rows summary">
        <Box fontWeight="bold">
          {mode === "full" ? "All rows" : result ? matching : "Filtered rows"}
        </Box>
        {mode === "filtered" && (
          <>
            {!result && <Box color="text-body-secondary">{matching}</Box>}
            <BoundedList
              label="conditions"
              items={filters.map((filter) => (
                <>
                  <strong>{filter.column}</strong>{" "}
                  {comparisonLabel(
                    filter.operator,
                    report.columnTypes?.[filter.column]
                  ).toLowerCase()}
                  {!textCaseSensitive &&
                    ["contains", "notContains"].includes(filter.operator) &&
                    " (case-insensitive)"}
                  {filter.value !== undefined && (
                    <>
                      {" "}
                      <span>
                        {filter.value === "" ? '"" (blank)' : filter.value}
                      </span>
                    </>
                  )}
                  {filter.values && (
                    <>
                      {" "}
                      <span>
                        {filter.values
                          .map((value) =>
                            value === "" ? '"" (blank)' : JSON.stringify(value)
                          )
                          .join(", ")}
                      </span>
                    </>
                  )}
                </>
              ))}
            />
            {incomplete > 0 && (
              <Box color="text-body-secondary">
                {incomplete} incomplete{" "}
                {incomplete === 1 ? "filter" : "filters"}
              </Box>
            )}
          </>
        )}
      </section>
      <section aria-label="Columns summary">
        <Box fontWeight="bold">
          {allColumns ? "All columns" : "Selected columns"} ({columns.length})
        </Box>
        <BoundedList label="columns" ordered items={columns} />
        {!columns.length && (
          <Box color="text-body-secondary">No columns selected.</Box>
        )}
      </section>
      {!result && (
        <div className="oms-settings-summary__note">
          <Box color="text-body-secondary" fontSize="body-s">
            One CSV in a ZIP. Matching rows are calculated during preparation.
          </Box>
        </div>
      )}
    </div>
  );
  if (standalone) return contents;
  return result ? (
    <ExpandableSection
      variant="container"
      headingTagOverride="h3"
      headerText="Settings used"
      expanded={resultExpanded}
      onChange={({ detail }) => setResultExpanded(detail.expanded)}
    >
      {resultExpanded && contents}
    </ExpandableSection>
  ) : (
    <Container header={<Header variant="h3">Export summary</Header>}>
      {contents}
    </Container>
  );
}
